/**
 * Cart line-item media helpers for the custom-photo poster feature.
 *
 * Loaded before theme.js in layout/theme.liquid so the cart drawer's view-model builder
 * can use them. Kept in their own file rather than inline in theme.js so the logic is
 * unit-testable (see custom-photo-cart.test.html) and the shared file's diff stays tiny.
 *
 * Both functions are no-ops for ordinary line items: customPhotoLineMedia returns null so
 * the caller keeps its existing behaviour, and stripCustomPhotoProperty only ever removes
 * the `Image` key.
 *
 * Media precedence, matching templates/cart.liquid so drawer and cart page agree:
 * uploaded photo > the line's own product photo > frame swatch chip > nothing.
 */
(function () {
  window.theme = window.theme || {};

  // Same CDN base the frame swatches already use in snippets/variant-button.liquid.
  var SWATCH_BASE = 'https://cdn.shopify.com/s/files/1/0521/3831/1842/t/2/assets/';

  // Every frame SKU starts with this; the two characters after it are the size code.
  var FRAME_SKU_PREFIX = 'R1-';

  // Colour code -> swatch chip slug. Only the colours that actually have a chip on the CDN
  // belong here (see snippets/variant-button.liquid); the rest render their product photo.
  var FRAME_SWATCH_SLUGS = { S: 'sort', H: 'hvid', E: 'lys-eg' };

  /**
   * Thumbnail URL for a /cart.js line item, or null to fall back to default handling.
   *  - Custom-photo lines carry an `Image` property: the customer's uploaded JPEG. Highest
   *    priority, because those products have no product photo of their own.
   *  - Frame lines are recognised by their SKU, not their handle. Frames are individual
   *    products — one per colour AND size — sharing the SKU shape R1-<SIZE><COLOUR>, e.g.
   *    R1-A3S (A3, sort) or R1-B2GR (B2, gammelrosa). Size is always the two characters
   *    after 'R1-'; the colour code is everything after that. SKUs are used rather than
   *    handles because a handle can be renamed in admin without anything noticing.
   *  - A frame that HAS a product photo returns null so the caller renders that photo. The
   *    merchant wants the frame's own base image in the cart, and the 50px chip is a
   *    corner crop that bleeds out of the drawer's thumbnail box when blown up. The swatch
   *    is therefore only a last resort, for a frame whose product carries no image at all —
   *    and only for the three original colours, the only ones with a chip on the CDN.
   *    This mirrors the `item.image == blank` guard in templates/cart.liquid, which the
   *    full cart page has always had.
   */
  theme.customPhotoLineMedia = function (item) {
    if (!item) return null;

    var props = item.properties;
    if (props && props.Image) return props.Image;

    var sku = String(item.sku || '').toUpperCase();
    // Prefix match, not `indexOf(...) !== -1`, so a SKU that merely contains "R1-"
    // somewhere in the middle is not mistaken for a frame.
    if (sku.indexOf(FRAME_SKU_PREFIX) !== 0) return null;

    // The frame's own photo wins over the chip; null hands the caller back to product.image.
    if (item.image) return null;

    // Exact lookup on the whole colour code: 'G' (gul) and 'GR' (gammelrosa) are different
    // frames, so a startsWith test here would hand gammelrosa the wrong swatch.
    var slug = FRAME_SWATCH_SLUGS[sku.slice(FRAME_SKU_PREFIX.length + 2)];
    return slug ? SWATCH_BASE + 'ramme-i-' + slug + '_50x.png' : null;
  };

  /**
   * Remove the `Image` property so the uploaded file's name isn't printed as text
   * beneath the thumbnail it already renders as. It stays on the order for the operator.
   * Mutates in place, matching how the caller already strips underscore-prefixed keys.
   */
  theme.stripCustomPhotoProperty = function (properties) {
    if (properties && Object.prototype.hasOwnProperty.call(properties, 'Image')) {
      delete properties.Image;
    }
    return properties;
  };
})();
